import React, { useEffect, useState } from "react";
import "./../styles/components/pricingCard.css";
import { CheckCircle, Rocket } from "lucide-react";
import { AuthService } from "../services/auth";
import { getApiBase } from "../config/api";
import { useAuth } from "../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { showToast } from '../utils/toastUtils';

interface Plan {
  name: string;
  price: string;
  amount?: number;
  coins: number;
  description: string;
  isFree?: boolean;
  features?: string[]; // Thêm mảng features để chứa các bullet points
}

const PricingCard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activePlan, setActivePlan] = useState("Basic");
  const [currentPlan, setCurrentPlan] = useState<string | null>(null);
  const [plans, setPlans] = useState<Plan[]>([
    // Dự phòng (sẽ được thay thế bằng API)
    {
      name: "Basic",
      price: "Miễn phí",
      amount: 0,
      coins: 4,
      description: "Dùng thử miễn phí — trải nghiệm giới hạn 4 token mỗi ngày.",
      isFree: true,
      features: [
        "Token: 4 token / ngày (tự động làm mới mỗi ngày)",
        "Giới hạn: Tối đa 4 token tại mọi thời điểm",
        "Thời hạn: Vĩnh viễn",
        "Cộng dồn: Không (mỗi ngày tự reset về 4 token)",
      ],
    },
    {
      name: "Premium",
      price: "5.000 point",
      amount: 5000,
      coins: 10,
      description:
        "Mở rộng giới hạn sử dụng — mua 10 token, dùng bất kỳ lúc nào",
      features: [
        "Token: 10 token (mua một lần)",
        "Giới hạn: Không giới hạn thời gian sử dụng",
        "Thời hạn: Không thời hạn",
        "Cộng dồn: Có (có thể mua thêm token bất cứ lúc nào)",
      ],
    },
    {
      name: "Advance",
      price: "50.000 point",
      amount: 50000,
      coins: 100,
      description:
        "Phù hợp cho doanh nghiệp nhỏ — linh hoạt, tiết kiệm chi phí dài hạn.",
      features: [
        "Token: 100 token (mua một lần)",
        "Giới hạn: Không giới hạn thời gian sử dụng",
        "Thời hạn: Không thời hạn",
        "Cộng dồn: Có (có thể mua thêm token bất cứ lúc nào)",
      ],
    },
    {
      name: "Super",
      price: "500.000 point",
      amount: 500000,
      coins: 1000,
      description:
        "Giải pháp toàn diện cho doanh nghiệp — tối ưu cho nhu cầu lớn và liên tục.",
      features: [
        "Token: 1000 token (mua một lần)",
        "Giới hạn: Không giới hạn thời gian sử dụng",
        "Thời hạn: Không thời hạn",
        "Cộng dồn: Có (có thể mua thêm token bất cứ lúc nào)",
      ],
    },
  ]);
  const [showModal, setShowModal] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<null | {
    transaction: any;
    bank: {
      bin: string;
      bankName: string;
      accountName: string;
      accountNumber: string;
      content: string;
      amount: number;
      vietQrUrl: string;
    };
  }>(null);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const apiBase = getApiBase();
        const res = await fetch(`${apiBase}/payments/packages`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const apiPackages = (data?.packages || []) as Array<{
          id: string;
          name: string;
          price: number;
          priceText?: string;
          duration?: string;
          features?: string[];
          coins?: number;
          description?: string;
          isFree?: boolean;
        }>;

        const defaultFeatures = [
          "Token: 4 token / ngày (tự động làm mới mỗi ngày)",
          "Giới hạn: Tối đa 4 token tại mọi thời điểm",
          "Thời hạn: Vĩnh viễn",
          "Cộng dồn: Không (mỗi ngày tự reset về 4 token)",
        ];

        const formatted: Plan[] = apiPackages.map((p) => ({
          name: p.name,
          price:
            typeof p.price === "number"
              ? new Intl.NumberFormat("vi-VN").format(p.price) + " point"
              : (p.priceText || "")
                  .replace(/[₫]/g, "")
                  .replace(/\bVND\b/gi, "point")
                  .trim(),
          amount: typeof p.price === "number" ? p.price : undefined,
          coins:
            typeof p.coins === "number"
              ? p.coins
              : p.id === "token10"
              ? 10
              : p.id === "token100"
              ? 100
              : p.id === "token1000"
              ? 1000
              : 0,
          description:
            p.description ?? (p.id === "free" ? "Dùng thử miễn phí" : p.name),
          isFree: p.isFree,
          features: p.features || defaultFeatures, // Gán giá trị mặc định nếu API không cung cấp features
        }));

        setPlans(formatted);
      } catch (e: any) {
        // Handle error silently
      }
    };
    fetchPackages();
  }, []);

  // Lấy gói hiện tại của tài khoản để highlight đúng
  useEffect(() => {
    const fetchCurrentPackage = async () => {
      try {
        if (!user) return; // Không có người dùng, bỏ qua
        // Admin/Moderator: không có gói hiện tại (vô hạn)
        const role = String((user as any).role || "").toLowerCase();
        if (role === "admin" || role === "moderator") return;
        const apiBase = getApiBase();
        const res = await fetch(`${apiBase}/payments/current-package`, {
          headers: { ...AuthService.getAuthHeaders() },
          credentials: "include",
        });
        if (!res.ok) return;
        const data = await res.json();

        const name: string | undefined = data?.packageName;
        if (name && ["Basic", "Premium", "Advance", "Super"].includes(name)) {
          setCurrentPlan(name);
          setActivePlan(name);
        }
      } catch {}
    };
    fetchCurrentPackage();
  }, [user]);

  // Xác định nội dung nút dựa trên gói active
  const getButtonText = () => {
    if (activePlan === "Basic") return "Nâng cấp ngay";
    if (activePlan === "Premium") return "Mua gói Premium";
    if (activePlan === "Advance") return "Mua gói Advance";
    if (activePlan === "Super") return "Mua gói Super";
    return "Mua gói Advance";
  };

  return (
    <div className="pricing-container">
      <div className="plans-section">
        {plans.map((plan) => (
          <div
            key={plan.name}
            className={`plan-card ${activePlan === plan.name ? "active" : ""}`}
            onClick={() => setActivePlan(plan.name)}
          >
            <input
              type="radio"
              id={plan.name}
              name="plan"
              checked={activePlan === plan.name}
              onChange={() => setActivePlan(plan.name)}
            />
            <label htmlFor={plan.name}>
              <span className="plan-name">{plan.name}</span>
              <span className="plan-price">{plan.price}</span>
              {currentPlan === plan.name &&
                !(
                  String((user as any)?.role || "").toLowerCase() === "admin" ||
                  String((user as any)?.role || "").toLowerCase() ===
                    "moderator"
                ) && <span className="free-tag">Gói hiện tại</span>}
              <span className="plan-coins">
                {plan.coins} <span className="coin-icon">🌟</span>
              </span>
              <span className="plan-description">{plan.description}</span>
            </label>
          </div>
        ))}
      </div>
      <div className="card-section">
        <div className="custom-card">
          <div className="card-overlay"></div>
          <div className="custom-card-content">
            <h3>
              {plans.find((p) => p.name === activePlan)?.name ||
                "Gói trải nghiệm"}
            </h3>
            <p>
              {plans.find((p) => p.name === activePlan)?.description ||
                "Dùng thử đăng backlink miễn phí"}
            </p>
            <ul>
              {plans
                .find((p) => p.name === activePlan)
                ?.features?.map((feature, index) => (
                  <li key={index}>
                    <CheckCircle size={14} /> {feature}
                  </li>
                ))}
            </ul>
            {activePlan !== "Basic" && (
              <button
                type="button"
                className="button-link"
                onClick={async () => {
                  try {
                    // Nếu chưa đăng nhập, chuyển tới trang đăng nhập
                    if (!user) {
                      const redirect = location.pathname;
                      navigate(
                        `/dang-nhap?redirect=${encodeURIComponent(redirect)}`
                      );
                      return;
                    }
                    const current =
                      plans.find((p) => p.name === activePlan) || plans[0];
                    const packageType =
                      current.coins === 10
                        ? "token10"
                        : current.coins === 100
                        ? "token100"
                        : current.coins === 1000
                        ? "token1000"
                        : "token10";
                    const apiBase = getApiBase();
                    const res = await fetch(`${apiBase}/payments/bank/create`, {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        ...AuthService.getAuthHeaders(),
                      },
                      body: JSON.stringify({ packageType }),
                    });
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    const data = await res.json();
                    setPaymentInfo(data);
                    setShowModal(true);
                  } catch (e) {
                    showToast.error(
                      "Không thể khởi tạo thanh toán. Vui lòng đăng nhập và thử lại."
                    );
                  }
                }}
              >
                {getButtonText()} <Rocket size={16} />
              </button>
            )}
          </div>
        </div>
      </div>
      {showModal && paymentInfo && (
        <PaymentModal
          onClose={() => {
            setShowModal(false);
            setPaymentInfo(null);
          }}
          plan={plans.find((p) => p.name === activePlan) || plans[0]}
          payment={paymentInfo}
          userEmail={user?.email || ""}
        />
      )}
    </div>
  );
};

export default PricingCard;

type PaymentModalProps = {
  onClose: () => void;
  plan: Plan;
  payment: {
    transaction: any;
    bank: {
      bin: string;
      bankName: string;
      accountName: string;
      accountNumber: string;
      content: string;
      amount: number;
      vietQrUrl: string;
    };
  };
  userEmail: string;
};

const PaymentModal: React.FC<PaymentModalProps> = ({
  onClose,
  plan,
  payment,
  userEmail,
}) => {
  const amount =
    payment.bank.amount ??
    plan.amount ??
    (plan.price ? Number((plan.price || "").replace(/[^0-9]/g, "")) : 0);
  const formatVnd = (n: number) =>
    `${new Intl.NumberFormat("vi-VN").format(n)} point`;
  const txCode = payment.transaction?.transactionCode;
  const bank = payment.bank;
  const vietQrUrl = bank.vietQrUrl;

  const formatAccount = (acc: string) =>
    acc
      .replace(/\D/g, "")
      .replace(/(\d{3})(?=\d)/g, "$1 ")
      .trim();

  const copy = async (text?: string) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {}
  };

  return (
    <div className="pm-backdrop">
      <div className="pm-modal">
        <button aria-label="Đóng" className="pm-close-icon" onClick={onClose}>
          ✕
        </button>
        <div className="pm-col">
          <h3 className="pm-title">Thông tin gói cước</h3>
          <Row
            label="Tên gói cước"
            value={<span className="pm-blue">{plan.name}</span>}
          />
          <Row
            label="Giá trị thanh toán"
            value={<span className="pm-amount">{formatVnd(amount)}</span>}
          />
          <Row
            label="Token nhận được"
            value={
              <span className="pm-blue">{`${plan.coins.toLocaleString(
                "vi-VN"
              )}`}</span>
            }
            extra={<span className="coin-icon"></span>}
          />
          {txCode && (
            <Row
              label="Mã giao dịch"
              value={<span className="pm-blue">{txCode}</span>}
              extra={
                <button
                  onClick={() => copy(txCode)}
                  className="pm-copy"
                  title="Copy"
                ></button>
              }
            />
          )}
          <Row
            label="Nhà cung cấp"
            value={
              <a
                className="pm-link"
                href="https://sdtc.vn"
                target="_blank"
                rel="noreferrer"
              >
                {bank.accountName}
              </a>
            }
          />
          <div className="pm-sep" />
          <Row
            label="Tên TK"
            value={<span className="pm-blue">{bank.accountName}</span>}
          />
          <Row
            label="STK"
            value={
              <span className="pm-blue">
                {formatAccount(bank.accountNumber)}
              </span>
            }
          />
          <Row
            label="Ngân hàng"
            value={<span className="pm-blue">{bank.bankName}</span>}
          />
          <Row
            label="Nội dung"
            value={<span className="pm-blue">{bank.content}</span>}
          />
        </div>

        <div className="pm-qr">
          <img src={vietQrUrl} alt="VietQR" />
          <div className="pm-note">
            Vui lòng kiểm tra đúng thông tin trước khi thanh toán
          </div>
          <div className="pm-email">
            Kiểm tra hoá đơn sau khi thanh toán xong tại:{" "}
            <a className="pm-link" href={`mailto:${userEmail}`}>
              {userEmail}
            </a>
          </div>
          <button
            className="doneBtn"
            onClick={async () => {
              try {
                const packageType =
                  plan.coins === 10
                    ? "token10"
                    : plan.coins === 100
                    ? "token100"
                    : plan.coins === 1000
                    ? "token1000"
                    : "token10";
                const apiBase = getApiBase();
                const res = await fetch(`${apiBase}/payments/bank/complete`, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    ...AuthService.getAuthHeaders(),
                  },
                  credentials: "include",
                  body: JSON.stringify({ packageType }),
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                showToast.success("Đã gửi thông tin. Giao dịch đang chờ xét duyệt.");
                onClose();
              } catch (e) {
                showToast.error("Không thể gửi thông tin. Vui lòng thử lại.");
              }
            }}
          >
            Hoàn thành
          </button>
        </div>
      </div>
    </div>
  );
};

const Row: React.FC<{
  label: string;
  value: React.ReactNode;
  extra?: React.ReactNode;
  linkHref?: string;
}> = ({ label, value, extra, linkHref }) => (
  <div className="pm-row">
    <div className="pm-row-label">{label}</div>
    <div className="pm-row-val">
      {linkHref ? (
        <a href={linkHref} target="_blank" rel="noreferrer">
          {value}
        </a>
      ) : (
        <span>{value}</span>
      )}
      {extra}
    </div>
  </div>
);
